import React from "react";
import {
  Building,
  HeartPulse,
  ShoppingCart,
  Factory,
  Truck,
  GraduationCap,
  Cloud,
  ShieldCheck,
  LineChart,
  Globe,
  Sparkles,
  CheckCircle2,
  Trophy,
  Star,
  Plus,
  Minus,
  FileText,
  ArrowUpRight,
} from "lucide-react";

type Pillar = {
  title: string;
  description: string;
  bullets: string[];
};

type UseCase = {
  title: string;
  description: string;
  impact: string;
};

type Snapshot = {
  label: string;
  value: string;
  hint?: string;
};

type CaseStudy = {
  label: string;
  title: string;
  summary: string;
  highlight: string;
  result: string;
};

type Industry = {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  tagline: string;
  overview: string;
  sectors: string[];
  snapshots: Snapshot[];
  challenges: string[];
  pillars: Pillar[];
  useCases: UseCase[];
  techStack: string[];
  dataSources: string[];
  engagementModels: string[];
  caseStudy: CaseStudy;
};

type FAQ = {
  question: string;
  answer: string;
};

const INDUSTRIES: Industry[] = [
  {
    id: "financial-services",
    name: "Financial Services & Fintech",
    icon: Building,
    tagline: "From periodic reporting to real-time, risk-aware decisions.",
    overview:
      "We help banks, NBFCs, insurers and fintechs modernize their data stack, automate manual processes and embed AI into every customer and risk decision.",
    sectors: [
      "Retail & Digital Banking",
      "Lending & NBFCs",
      "Insurance & InsurTech",
      "Wealth, Trading & neo-brokers",
    ],
    snapshots: [
      {
        label: "Time-to-approve loans",
        value: "↓ 40–60%",
        hint: "with document & risk automation",
      },
      {
        label: "Collections efficiency",
        value: "↑ 15–25%",
        hint: "through ML-driven strategy",
      },
      {
        label: "Fraud losses",
        value: "↓ 20–30%",
        hint: "via real-time anomaly detection",
      },
    ],
    challenges: [
      "Risk & fraud teams working with siloed, batch-only data.",
      "Credit decisions depending on manual rule tuning and spreadsheets.",
      "Fragmented view of the customer across cards, loans, deposits and channels.",
      "Regulatory reporting (RBI, IRDAI, SEBI, PCI-DSS) done as a fire-drill at month-end.",
    ],
    pillars: [
      {
        title: "Risk, Fraud & Collections Analytics",
        description:
          "Detect bad actors earlier and optimize collections while protecting genuine customers.",
        bullets: [
          "Real-time transaction scoring using supervised & unsupervised ML models.",
          "Behavioural scorecards updated continuously instead of yearly recalibration.",
          "Propensity-to-pay, contact-channel and promise-to-keep models for collections.",
          "Case management dashboards for investigators and field agents.",
        ],
      },
      {
        title: "Customer 360 & Personalisation",
        description:
          "Unify product, channel and behavioural data to orchestrate contextual experiences.",
        bullets: [
          "Customer 360 data models across CASA, cards, loans and investments.",
          "Next-best-action engines for web, app, branch and contact centre.",
          "Segmentation using RFM, lifetime value and behavioural clusters.",
          "Real-time offers, cross-sell and retention journeys driven by event streams.",
        ],
      },
      {
        title: "Reg-Tech & Data Platforms",
        description:
          "Make compliance and reporting a continuous, automated outcome of a strong data platform.",
        bullets: [
          "Modern data warehouse/lakehouse on Snowflake, BigQuery, Redshift or Databricks.",
          "Data quality, lineage and access controls baked into the platform.",
          "Pre-built regulatory reporting data marts and semantic layers.",
          "Audit-ready controls for PII, consent and encryption at rest & in transit.",
        ],
      },
    ],
    useCases: [
      {
        title: "Real-time Loan Origination Analytics",
        description:
          "Combine bureau, bank-statement, device and behavioural data to approve loans in seconds.",
        impact:
          "Improved approval rates without increasing portfolio risk; 50–70% reduction in manual underwriter effort.",
      },
      {
        title: "Collections War-Room Dashboard",
        description:
          "Portfolio-level view of buckets, flow rates, call centre performance and strategy experiments.",
        impact:
          "Faster response to slippages, 10–15% better recovery in early buckets and clearer regulator-friendly reporting.",
      },
    ],
    techStack: [
      "Spark & PySpark pipelines for large-volume processing",
      "MLflow for model lifecycle management",
      "Snowflake / BigQuery / Redshift as centralized warehouses",
      "Kafka / PubSub for real-time streaming",
      "dbt for semantic modelling and governance",
    ],
    dataSources: [
      "Core banking & LOS/LMS systems",
      "Bureau and alternative credit data",
      "Digital channels (web, app, internet banking)",
      "Payment gateways and UPI/IMPS/RTP rails",
      "Call-centre & CRM interaction logs",
    ],
    engagementModels: [
      "Platform diagnostics & roadmap in 4–6 weeks.",
      "Co-build pilot models with your in-house data team.",
      "Managed analytics squads embedded with risk & business.",
      "Ongoing model monitoring, refresh and MLOps support.",
    ],
    caseStudy: {
      label: "Featured BFSI case study",
      title: "NBFC cuts loan approval time from days to minutes",
      summary:
        "We worked with a fast-growing NBFC to unify bureau, bank statement and alternate data into a single decision engine.",
      highlight: "40–60% faster approvals",
      result:
        "Manual underwriting dropped for low-risk segments while portfolio risk stayed flat – enabling the team to launch new products faster.",
    },
  },
  {
    id: "healthcare-life-sciences",
    name: "Healthcare & Life Sciences",
    icon: HeartPulse,
    tagline: "AI that supports clinicians, never replaces them.",
    overview:
      "We work with hospitals, diagnostic networks and health-tech platforms to improve operational efficiency, clinical decision-support and patient experience without compromising on safety and privacy.",
    sectors: [
      "Multi-speciality hospitals",
      "Diagnostic labs & radiology chains",
      "Digital health & tele-medicine platforms",
      "Pharma & life-sciences analytics teams",
    ],
    snapshots: [
      {
        label: "OP/IP operations",
        value: "↑ 10–20% throughput",
        hint: "with capacity analytics",
      },
      {
        label: "Report TAT",
        value: "↓ 25–40%",
        hint: "via workflow optimisation",
      },
      {
        label: "No-show rates",
        value: "↓ 10–15%",
        hint: "through reminder & propensity models",
      },
    ],
    challenges: [
      "Highly variable patient inflow leading to bottlenecks in admissions, labs and pharmacy.",
      "Clinicians having to manually stitch information from EMR, LIS, RIS and PACS systems.",
      "Limited on-ground analytics teams and legacy HIS/LIS systems.",
      "Strict requirements around PHI security, HIPAA and local data-protection regulations.",
    ],
    pillars: [
      {
        title: "Operational & Capacity Analytics",
        description:
          "Match capacity to demand while keeping clinical priorities at the centre.",
        bullets: [
          "Forecast OPD/IPD inflow by speciality, time of day and day of week.",
          "Bed-occupancy, OT-utilisation and staff-roster optimisation.",
          "Turnaround time analytics for labs, imaging and pharmacy.",
          "Command-centre dashboards for hospital administrators.",
        ],
      },
      {
        title: "Clinical Decision Support & Triage",
        description:
          "Augment clinicians with AI assistance in triage, risk-stratification and imaging workflows.",
        bullets: [
          "Risk scores for readmission, deterioration and ICU transfer.",
          "AI-assisted triage for emergency, tele-consults and digital symptom checkers.",
          "Workflow orchestration for radiology (worklist prioritisation) and pathology.",
          "Configurable guard-rails and explanations for model outputs.",
        ],
      },
      {
        title: "Patient 360 & Engagement",
        description:
          "Understand the full patient journey across appointments, labs, pharmacy and digital touch-points.",
        bullets: [
          "Patient 360 views with visit history, clinical events and engagement signals.",
          "Personalised reminders, follow-up plans and wellness nudges.",
          "Campaign analytics for health-check packages and speciality programs.",
          "Feedback analysis from surveys, apps and social channels.",
        ],
      },
    ],
    useCases: [
      {
        title: "Hospital Operations Control Tower",
        description:
          "Integrated view of patient flow, occupancy and bottlenecks across departments and locations.",
        impact:
          "10–15% higher throughput with the same assets and staff; measurable reduction in patient wait-times.",
      },
      {
        title: "AI-assisted Tele-consult Platform",
        description:
          "Structured data capture and triage recommendations during tele-consults, integrated with existing HIS/EMR.",
        impact:
          "Higher quality of documentation, lower clinical risk, better ability to run longitudinal outcomes analytics.",
      },
    ],
    techStack: [
      "FHIR-compatible data models and APIs",
      "Secure data-lakes on cloud (GCP/AWS/Azure)",
      "De-identification and tokenisation pipelines for PHI",
      "MLOps frameworks with strict audit logs & approvals",
      "Dashboards with role-based access for clinical & admin teams",
    ],
    dataSources: [
      "HIS/EMR, LIS, RIS and PACS systems",
      "Wearables, remote monitoring and IoT devices",
      "Tele-consult and call-centre platforms",
      "Patient apps, portals and CRM tools",
      "Insurance & claims data where available",
    ],
    engagementModels: [
      "Discovery assessments with clinical & operations teams.",
      "Joint solution design with hospital IT and OEMs.",
      "Build-operate-transfer analytics pods.",
      "Training and enablement for internal data teams.",
    ],
    caseStudy: {
      label: "Featured healthcare case study",
      title: "Multi-speciality hospital builds real-time ops cockpit",
      summary:
        "A leading hospital network wanted live visibility into beds, OTs and diagnostics across locations.",
      highlight: "15% higher throughput",
      result:
        "By combining HIS, LIS and PACS feeds into one command centre, they reduced bottlenecks and cut patient wait times significantly.",
    },
  },
  {
    id: "retail-ecommerce",
    name: "Retail & E-Commerce",
    icon: ShoppingCart,
    tagline: "Every interaction becomes a learning signal.",
    overview:
      "We help marketplaces, D2C brands and omni-channel retailers turn clickstreams and transaction data into growth engines through experimentation, personalisation and intelligent operations.",
    sectors: [
      "Marketplaces & aggregators",
      "D2C & subscription brands",
      "Grocery & quick-commerce",
      "Fashion, lifestyle & electronics",
    ],
    snapshots: [
      {
        label: "Conversion rate",
        value: "↑ 5–15%",
        hint: "via better relevance & UX",
      },
      {
        label: "Marketing ROI",
        value: "↑ 20–30%",
        hint: "through attribution & LTV models",
      },
      {
        label: "Returns & cancellations",
        value: "↓ 8–12%",
        hint: "with quality & promise analytics",
      },
    ],
    challenges: [
      "Fragmented view of customers across web, app, store and marketplace channels.",
      "Promotions run without clear understanding of true incremental impact.",
      "Manual merchandising and rule-based sorting in search & browse.",
      "Supply-chain blind spots leading to stock-outs or over-stocking.",
    ],
    pillars: [
      {
        title: "Customer & Growth Analytics",
        description:
          "Understand who your best customers are, how you acquire them and how to retain them.",
        bullets: [
          "RFM and cohort analysis across acquisition channels.",
          "Customer lifetime value models driving bidding and budget allocation.",
          "Attribution frameworks that balance last-click with incrementality.",
          "Experimentation platform design (A/B testing, hold-outs, CUPED).",
        ],
      },
      {
        title: "Search, Browse & Recommendations",
        description:
          "Help each visitor discover the right products through ranking, recommendations and content.",
        bullets: [
          "Personalised home, category and product-detail pages.",
          "Search relevance models incorporating text, image and behaviour.",
          "Frequently bought together and bundle recommendation engines.",
          "Ranking algorithms tuned for margin, availability and experience.",
        ],
      },
      {
        title: "Merchandising & Supply-Chain Analytics",
        description:
          "Connect demand signals with inventory, pricing and fulfilment decisions.",
        bullets: [
          "Assortment and demand forecasting at SKU-location granularity.",
          "Fill-rate, stock-out and substitution analytics.",
          "Price-elasticity and promo-response models.",
          "Logistics performance dashboards across partners and lanes.",
        ],
      },
    ],
    useCases: [
      {
        title: "Personalised Homepage & Campaigns",
        description:
          "Dynamic segments and content blocks that adapt based on intent, lifecycle stage and past behaviour.",
        impact:
          "Immediate lift in conversion and retention with the same marketing budget; richer first-party data for future models.",
      },
      {
        title: "End-to-end Funnel Analytics",
        description:
          "Unified view of acquisition, product discovery, cart and post-purchase journeys.",
        impact:
          "Clear visibility into bottlenecks, better prioritisation of UX and operations initiatives.",
      },
    ],
    techStack: [
      "Event streaming & clickstream pipelines",
      "Feature stores for real-time personalisation",
      "Recommendation systems with batch + online inference",
      "Modern BI tools for marketing & product teams",
      "Warehouse-first architectures for analytics & activation",
    ],
    dataSources: [
      "Web & app analytics (CDP, tag managers, SDKs)",
      "Order management & ERP systems",
      "Marketing platforms (Ads, email, push, affiliates)",
      "Supply-chain & logistics systems",
      "Customer support and feedback tools",
    ],
    engagementModels: [
      "Analytics & experimentation jump-start.",
      "Recommendation engine pilots and roll-out.",
      "Embedded squads for growth, product & analytics.",
      "Capability building for in-house data teams.",
    ],
    caseStudy: {
      label: "Featured retail case study",
      title: "D2C brand lifts repeat purchases with personalisation",
      summary:
        "We helped a high-growth D2C brand stitch together web, app and order data into a unified customer view.",
      highlight: "12% uplift in repeat orders",
      result:
        "Targeted journeys and recommendations drove higher basket value and better ROI on performance marketing.",
    },
  },
  {
    id: "manufacturing-industry4",
    name: "Manufacturing & Industry 4.0",
    icon: Factory,
    tagline: "Make every machine, line and plant measurable and optimisable.",
    overview:
      "We partner with manufacturers to connect shop-floor data, quality systems and enterprise planning into a single view that powers OEE, yield and reliability improvements.",
    sectors: [
      "Discrete manufacturing (auto, electronics, industrial)",
      "Process industries (chemicals, metals, cement)",
      "CPG, food & beverages",
      "OEMs and equipment providers",
    ],
    snapshots: [
      {
        label: "Unplanned downtime",
        value: "↓ 20–40%",
        hint: "with predictive maintenance",
      },
      {
        label: "First-time-right quality",
        value: "↑ 5–10%",
        hint: "via process analytics",
      },
      {
        label: "Energy & utilities cost",
        value: "↓ 8–15%",
        hint: "through consumption intelligence",
      },
    ],
    challenges: [
      "OT/IT silos between PLCs, SCADA, historians and ERP systems.",
      "Limited visibility into root causes for scrap, rework and breakdowns.",
      "Dependence on tribal knowledge instead of data-driven standard work.",
      "Manual reporting for OEE, quality and compliance KPIs.",
    ],
    pillars: [
      {
        title: "Connected Shop-floor & OEE Analytics",
        description:
          "Bring production, downtime and quality data into a unified, near real-time view.",
        bullets: [
          "Machine and line-level data ingestion from PLC/SCADA/IoT gateways.",
          "Standard OEE and loss-tree analytics across plants.",
          "Real-time alerts for speed, idle and minor-stop anomalies.",
          "Dashboards for plant managers, supervisors and CI teams.",
        ],
      },
      {
        title: "Predictive Maintenance & Reliability",
        description:
          "Move from time-based to condition-based maintenance strategies.",
        bullets: [
          "Asset-specific health scores using sensor, environmental and usage data.",
          "Remaining useful life (RUL) models and failure-risk prediction.",
          "Maintenance-planning tools integrated with existing CMMS/ERP.",
          "Feedback loops between failures, root-cause and model refreshes.",
        ],
      },
      {
        title: "Quality, Yield & Energy Analytics",
        description:
          "Optimise process parameters and resource usage for consistent outcomes.",
        bullets: [
          "SPC charts and process-capability analysis at scale.",
          "Golden-batch identification and parameter recommender systems.",
          "Energy-intensity monitoring by line, product and shift.",
          "What-if simulation tools for engineers and CI leads.",
        ],
      },
    ],
    useCases: [
      {
        title: "Plant Performance Cockpit",
        description:
          "Executive and plant-level view of OEE, quality and cost with drill-downs to line and SKU.",
        impact:
          "Faster decision-making and sustained CI programs grounded in data instead of anecdotes.",
      },
      {
        title: "IoT-driven Predictive Maintenance",
        description:
          "Sensor-driven models that predict failures and recommend interventions.",
        impact:
          "Reduced unplanned downtime, lower spare-parts inventory and better asset utilisation.",
      },
    ],
    techStack: [
      "IoT gateways and time-series databases",
      "Edge + cloud hybrid architectures",
      "Streaming analytics for near real-time insights",
      "ML models for anomaly detection and RUL",
      "BI & reporting for operations and leadership teams",
    ],
    dataSources: [
      "Machines, PLCs, SCADA and historians",
      "Quality inspection and lab systems",
      "ERP, MES and maintenance systems",
      "Energy meters and environmental sensors",
      "Manual logs and digital forms",
    ],
    engagementModels: [
      "Pilot line implementations to prove value.",
      "Multi-plant roll-outs with change-management support.",
      "Joint innovation programs with OEMs and partners.",
      "Long-term reliability & analytics COEs.",
    ],
    caseStudy: {
      label: "Featured manufacturing case study",
      title: "Global plant network boosts OEE with one cockpit",
      summary:
        "A discrete manufacturer wanted a common way to compare performance across multiple plants and lines.",
      highlight: "8–10 pt OEE improvement",
      result:
        "By streaming shop-floor data into a unified analytics layer, CI teams could prioritise the right losses and sustain gains over time.",
    },
  },
  {
    id: "logistics-supply-chain",
    name: "Logistics, Supply-Chain & Mobility",
    icon: Truck,
    tagline: "From network planning to last-mile, decisions backed by data.",
    overview:
      "We work with logistics providers, marketplaces and mobility platforms to increase on-time performance, reduce cost per shipment and improve customer experience.",
    sectors: [
      "3PLs & express logistics",
      "Hyperlocal & last-mile networks",
      "Fleet & mobility platforms",
      "E-commerce & omni-channel fulfilment",
    ],
    snapshots: [
      {
        label: "On-time performance",
        value: "↑ 5–12%",
        hint: "with routing & SLA analytics",
      },
      {
        label: "Cost per shipment",
        value: "↓ 8–15%",
        hint: "via network & asset optimisation",
      },
      {
        label: "Support tickets",
        value: "↓ 15–25%",
        hint: "through better promise & tracking",
      },
    ],
    challenges: [
      "Fragmented view of shipments across hubs, partners and modes.",
      "Limited data-driven routing and network-planning capabilities.",
      "Reactive rather than proactive exception management.",
      "Customer experience gaps in tracking, notifications and promises.",
    ],
    pillars: [
      {
        title: "Network, Route & Capacity Design",
        description:
          "Design networks that balance speed, reliability and cost at scale.",
        bullets: [
          "Lane, hub and zone analytics with volume and SLA overlays.",
          "Route design and optimisation using historical and real-time data.",
          "Capacity-planning tools for peak seasons and events.",
          "What-if simulations for contract and partner changes.",
        ],
      },
      {
        title: "Execution, Tracking & Exceptions",
        description:
          "Give operations teams and customers full visibility into shipments.",
        bullets: [
          "Shipment-level tracking with predictive ETA models.",
          "Event-stream processing for delay and risk signals.",
          "Exception-queues prioritised by customer and SLA impact.",
          "Self-serve tracking, communication and NPS tools.",
        ],
      },
      {
        title: "Cost & Profitability Analytics",
        description:
          "Understand true economics at lane, customer and shipment level.",
        bullets: [
          "Cost allocation models for fuel, tolls, handling and overheads.",
          "Customer and lane-level profitability dashboards.",
          "Contract-and rate-card analytics for sales teams.",
          "Scenario planning for new geographies and services.",
        ],
      },
    ],
    useCases: [
      {
        title: "Predictive ETA & Promise Engine",
        description:
          "Use network history and live signals to set realistic promises and update them proactively.",
        impact:
          "Higher on-time performance, fewer support calls and more repeat business.",
      },
      {
        title: "Operations Control Tower",
        description:
          "Central cockpit for live shipments, issues and resource deployment.",
        impact:
          "Faster recovery from disruptions and better utilisation of fleet and hubs.",
      },
    ],
    techStack: [
      "Geo-spatial data processing & mapping",
      "Routing engines and optimisation libraries",
      "Streaming platforms for events & telemetry",
      "Data-driven control tower dashboards",
      "Mobile-first tools for field operations",
    ],
    dataSources: [
      "TMS, WMS and OMS platforms",
      "Telematics and GPS devices",
      "Partner and carrier data feeds",
      "Support, NPS and feedback systems",
      "Pricing, contracts and rate-cards",
    ],
    engagementModels: [
      "Network diagnostics and opportunity sizing.",
      "Pilot implementations on priority lanes or regions.",
      "Embedded squads for control-tower build-out.",
      "Continuous analytics support and tuning.",
    ],
    caseStudy: {
      label: "Featured logistics case study",
      title: "Express network improves promise accuracy at scale",
      summary:
        "A national parcel network struggled with ETA accuracy during peaks and disruptions.",
      highlight: "25% fewer delayed shipments",
      result:
        "Predictive ETAs and exception queues helped ops teams react earlier and set better expectations with customers and partners.",
    },
  },
  {
    id: "saas-tech",
    name: "SaaS, Technology & Platforms",
    icon: Cloud,
    tagline: "Data & AI as first-class citizens in your product roadmap.",
    overview:
      "We support product and engineering teams in building analytics-first, AI-powered SaaS and platform experiences that customers love and pay for.",
    sectors: [
      "B2B & B2C SaaS",
      "Marketplaces & aggregators",
      "APIs and fintech/banking-as-a-service",
      "Developer tools & infra platforms",
    ],
    snapshots: [
      {
        label: "Feature adoption",
        value: "↑ 10–25%",
        hint: "through better activation journeys",
      },
      {
        label: "Expansion revenue",
        value: "↑ 15–30%",
        hint: "via usage & value analytics",
      },
      {
        label: "Churn",
        value: "↓ 5–10%",
        hint: "with health & risk signals",
      },
    ],
    challenges: [
      "Product analytics stitched together across multiple tools without a single source of truth.",
      "Low visibility into feature adoption and true value drivers for different customer segments.",
      "First versions of AI features built as one-off experiments instead of a platform.",
      "Limited data platform engineering bandwidth within the core product team.",
    ],
    pillars: [
      {
        title: "Product & Growth Analytics Foundations",
        description:
          "Build a warehouse-first analytics stack that your PMs, growth and CS teams can rely on.",
        bullets: [
          "Event-tracking design and governance with privacy-first defaults.",
          "Warehouse-native models for accounts, workspaces, seats and events.",
          "North-star and guard-rail metric frameworks.",
          "Self-serve dashboards and exploration tools for business users.",
        ],
      },
      {
        title: "Embedded AI Features",
        description:
          "Turn AI prototypes into reliable, observable features in your product.",
        bullets: [
          "Recommendation and ranking in feeds, search and content discovery.",
          "Smart defaults, auto-configuration and assistants inside the product.",
          "Feature-store & experimentation frameworks for AI components.",
          "Evaluation harnesses with offline and online metrics.",
        ],
      },
      {
        title: "Monetisation & Success Analytics",
        description:
          "Connect usage, value and pricing to drive sustainable growth.",
        bullets: [
          "Onboarding and activation funnels for new accounts and users.",
          "Account-health scores powering CS workflows and playbooks.",
          "Pricing and packaging experiments grounded in real usage data.",
          "Revenue, churn and expansion analytics at segment and cohort level.",
        ],
      },
    ],
    useCases: [
      {
        title: "Analytics-first Data Platform",
        description:
          "Central data model and metrics layer for all go-to-market, CS and finance reporting.",
        impact:
          "One source of truth across teams, faster decision-making and simpler tool integration.",
      },
      {
        title: "In-product AI Assistant",
        description:
          "Context-aware copilots that help users set up, configure and use complex workflows.",
        impact:
          "Higher activation, lower support burden and clear differentiation in the market.",
      },
    ],
    techStack: [
      "Warehouse-first analytics (Snowflake, BigQuery, Redshift, Databricks)",
      "Modern ELT (Fivetran, Airbyte, custom pipelines)",
      "dbt for modelling and governance",
      "Feature stores and experimentation platforms",
      "Observability & quality tools for production data",
    ],
    dataSources: [
      "Product and event-tracking tools",
      "Billing & subscription systems",
      "CRM and marketing automation",
      "Support, knowledge-base and community platforms",
      "Platform and infra telemetry",
    ],
    engagementModels: [
      "Analytics stack blueprint and implementation.",
      "AI feature discovery and prioritisation workshops.",
      "Joint squads with product & engineering for build-out.",
      "Ongoing analytics and MLOps partnerships.",
    ],
    caseStudy: {
      label: "Featured SaaS case study",
      title: "B2B SaaS platform builds analytics-first GTM engine",
      summary:
        "A PLG SaaS company wanted one source of truth for product, revenue and success metrics.",
      highlight: "Single metrics layer across teams",
      result:
        "With a warehouse-native stack and shared metric definitions, GTM teams could finally agree on numbers and move faster on experiments.",
    },
  },
  {
    id: "education-edtech",
    name: "Education, EdTech & Talent Platforms",
    icon: GraduationCap,
    tagline: "Close the skills loop from enrolment to employability.",
    overview:
      "We help universities, training providers and talent platforms understand learner journeys, improve completion rates and connect skills to real career outcomes.",
    sectors: [
      "Universities & colleges",
      "Bootcamps & training companies",
      "Online learning & MOOCs",
      "Talent & career platforms",
    ],
    snapshots: [
      {
        label: "Course completion",
        value: "↑ 8–20%",
        hint: "with risk & engagement models",
      },
      {
        label: "Placement outcomes",
        value: "↑ 10–25%",
        hint: "via better matching & mentoring",
      },
      {
        label: "Learner NPS",
        value: "↑ 15–30 pts",
        hint: "through personalised journeys",
      },
    ],
    challenges: [
      "Drop-offs across enrolment, course and placement stages.",
      "Limited visibility into which interventions actually help learners succeed.",
      "Fragmented systems for LMS, CRM, assessments and placement tracking.",
      "Need to report outcomes transparently to learners and partners.",
    ],
    pillars: [
      {
        title: "Learner Journey Analytics",
        description:
          "Track and optimise the full lifecycle from first touch to alumni.",
        bullets: [
          "Funnel analytics across marketing, enrolment and onboarding.",
          "Engagement scores based on activity, assessments and behaviour.",
          "Early warning signals for at-risk learners.",
          "Success-stories and outcomes analytics by cohort and program.",
        ],
      },
      {
        title: "Personalised Learning & Support",
        description:
          "Use AI to scale mentoring and support without losing the human touch.",
        bullets: [
          "Content and assignment recommendations by skill-gaps.",
          "Mentor-matching and session-recommendation engines.",
          "Question-answering assistants grounded in your own content.",
          "Scheduling, reminders and nudges optimised by response patterns.",
        ],
      },
      {
        title: "Employability & Talent Analytics",
        description:
          "Connect learning, skill signals and hiring outcomes into one view.",
        bullets: [
          "Skill graphs built from curriculum, projects and assessments.",
          "Job-role and candidate-matching models.",
          "Placement pipeline analytics for partners and employers.",
          "Outcome dashboards for leadership and external stakeholders.",
        ],
      },
    ],
    useCases: [
      {
        title: "Cohort Health & Risk Dashboards",
        description:
          "Live view of which learners and cohorts need attention, and why.",
        impact:
          "Higher completion rates and better learner experience with the same staff capacity.",
      },
      {
        title: "Career & Placement Analytics",
        description:
          "Understand what drives job outcomes and how to better support learners.",
        impact:
          "Stronger employer relationships, improved placement metrics and better program design.",
      },
    ],
    techStack: [
      "Data pipelines from LMS, CRM and assessment tools",
      "Warehouse & BI layer for academic and business teams",
      "ML models for risk, engagement and matching",
      "Gen-AI for content search and Q&A assistants",
      "Privacy and consent frameworks for learner data",
    ],
    dataSources: [
      "LMS & content platforms",
      "Marketing & admissions systems",
      "Assessment & proctoring tools",
      "Placement and employer-tracking systems",
      "Surveys, NPS and feedback channels",
    ],
    engagementModels: [
      "Discovery and roadmap workshops with academic & business teams.",
      "Pilot implementations on select programs or cohorts.",
      "Joint teams for analytics & platform build-out.",
      "Capability building for internal data, product and ops teams.",
    ],
    caseStudy: {
      label: "Featured education case study",
      title: "EdTech platform reduces learner drop-offs with risk scores",
      summary:
        "A career-focused EdTech platform needed early warning signals for at-risk learners across cohorts.",
      highlight: "18% improvement in completion",
      result:
        "Risk models and cohort dashboards helped mentors focus their time where it mattered most, improving outcomes and satisfaction.",
    },
  },
];

const INDUSTRY_FAQ: FAQ[] = [
  {
    question: "Which industries do you have the most experience in?",
    answer:
      "We work across BFSI, healthcare, retail, manufacturing, logistics, SaaS and education. For each of these, we have shipped production-grade data platforms and AI use-cases, not just POCs.",
  },
  {
    question: "How do you usually start an engagement?",
    answer:
      "Most clients start with a short discovery and blueprint, followed by 1–2 focused pilots. Once value is proven, we scale to a longer-term squad model embedded with your teams.",
  },
  {
    question: "Can you work with our existing data and cloud stack?",
    answer:
      "Yes. We plug into your existing tools and cloud – whether that is AWS, Azure, GCP or on-prem – and prefer to reuse what you already have instead of forcing new tooling.",
  },
  {
    question: "How do you handle security, compliance and PII?",
    answer:
      "We align to your security guidelines, follow least-privilege access, and design architectures with encryption, audit trails and data minimisation. For regulated industries, we work closely with your infosec teams.",
  },
  {
    question: "What does a typical project timeline look like?",
    answer:
      "A focused pilot runs for 8–12 weeks from discovery to first production release. Larger programs with multiple use-cases span several quarters with stable squads.",
  },
];

const Industries: React.FC = () => {
  const [activeId, setActiveId] = React.useState<string>(INDUSTRIES[0]?.id);
  const [openFaq, setOpenFaq] = React.useState<number | null>(0);

  const active = React.useMemo(
    () => INDUSTRIES.find((i) => i.id === activeId) ?? INDUSTRIES[0],
    [activeId]
  );

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900">
      {/* HERO – darker gradient card, reduced gap */}
      <section className="w-full pt-16 pb-10 bg-slate-50">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 px-6 py-8 md:px-10 md:py-12 shadow-[0_24px_60px_rgba(15,23,42,0.7)]">
            {/* dark glowing blobs */}
            <div className="pointer-events-none absolute -top-24 -right-10 h-56 w-56 rounded-full bg-sky-500/40 blur-3xl animate-pulse" />
            <div className="pointer-events-none absolute bottom-[-4rem] left-[-3rem] h-52 w-52 rounded-full bg-indigo-500/35 blur-3xl animate-pulse" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.15),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(129,140,248,0.35),transparent_60%)]" />

            <div className="relative max-w-4xl">
              <p className="text-xs tracking-[0.25em] uppercase text-sky-300">
                Industries we serve
              </p>
              <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold text-white">
                Data &amp; AI, tailored to your industry.
              </h1>
              <p className="mt-4 text-sm md:text-base text-slate-200 max-w-3xl">
                We partner with organisations across regulated, high-growth and operations-heavy
                industries. Our teams bring deep domain context, proven accelerators and
                production experience – so you do not have to choose between speed and robustness.
              </p>

              {/* gradient pills */}
              <div className="mt-6 flex flex-wrap gap-3 text-xs md:text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-400 px-3.5 py-1.5 text-[0.78rem] md:text-xs font-medium text-white shadow-lg ring-1 ring-white/30">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </span>
                  Regulated-industry ready
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-sky-400 px-3.5 py-1.5 text-[0.78rem] md:text-xs font-medium text-white shadow-lg ring-1 ring-white/30">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
                    <LineChart className="h-3.5 w-3.5" />
                  </span>
                  Outcome-first engagements
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 px-3.5 py-1.5 text-[0.78rem] md:text-xs font-medium text-white shadow-lg ring-1 ring-white/30">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
                    <Globe className="h-3.5 w-3.5" />
                  </span>
                  Distributed, global delivery
                </span>
              </div>

              <p className="mt-4 text-[0.8rem] md:text-xs text-slate-300 max-w-xl">
                From BFSI and healthcare to manufacturing, logistics and SaaS, we’ve seen the
                patterns of what works – and we adapt them to your stack, team and regulatory
                context.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="w-full bg-slate-900 text-slate-100 border-y border-slate-800">
        <div className="w-full px-4 sm:px-8 lg:px-16 py-4 md:py-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-xs md:text-sm">
            <p className="uppercase tracking-[0.22em] text-sky-400 text-[0.65rem]">
              Trusted by data-driven teams
            </p>
            <p className="mt-1 text-[0.78rem] md:text-sm text-slate-200">
              Startups and enterprises across India, the US and Europe work with Technocolabs to
              ship real data &amp; AI products.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-[0.7rem] md:text-xs">
            <span className="rounded-full border border-slate-700 bg-slate-800/70 px-3 py-1">
              30+ production deployments
            </span>
            <span className="rounded-full border border-slate-700 bg-slate-800/70 px-3 py-1">
              8+ industries
            </span>
            <span className="rounded-full border border-slate-700 bg-slate-800/70 px-3 py-1">
              Cloud-first, vendor-neutral
            </span>
          </div>
        </div>
      </section>

      {/* Main layout */}
      <section className="w-full pb-6 bg-slate-100/60">
        <div className="w-full px-4 sm:px-8 lg:px-16 grid gap-6 lg:gap-8 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
          {/* Left rail: single compact panel */}
          <aside className="rounded-2xl border border-slate-200 bg-white shadow-sm px-3 py-3 sm:px-4 sm:py-4 flex flex-col gap-4">
            {/* Industry selector */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                Choose your industry
              </p>
              <p className="mt-1.5 text-[0.72rem] text-slate-600">
                Switch between industries to see specific challenges, use-cases and tech stack.
              </p>

              <div className="mt-3 space-y-1.5">
                {INDUSTRIES.map((industry) => {
                  const Icon = industry.icon;
                  const isActive = industry.id === active.id;
                  return (
                    <button
                      key={industry.id}
                      type="button"
                      onClick={() => setActiveId(industry.id)}
                      className={`w-full text-left rounded-lg px-2.5 py-2 text-[0.8rem] flex items-start gap-2.5 transition ${
                        isActive
                          ? "bg-sky-50 border border-sky-300"
                          : "bg-slate-50 border border-transparent hover:border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md ${
                          isActive ? "bg-sky-100" : "bg-slate-200"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 text-sky-700" />
                      </span>
                      <span className="flex flex-col">
                        <span className="font-medium text-slate-900 leading-tight">
                          {industry.name}
                        </span>
                        <span className="text-[0.7rem] text-slate-500 leading-snug">
                          {industry.tagline}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Divider line */}
            <div className="h-px bg-slate-200" />

            {/* How we work */}
            <div>
              <p className="text-[0.7rem] font-semibold text-sky-700 uppercase tracking-[0.18em]">
                How we work
              </p>
              <ul className="mt-2 space-y-1.5 text-[0.76rem] text-slate-700">
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    Discovery &amp; blueprint with your domain, data and tech teams.
                  </span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    Pilot &amp; hardening in a narrow, high-ROI slice before wider roll-out.
                  </span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    Scale &amp; enable with documentation, training and CI/CD for data &amp; ML.
                  </span>
                </li>
              </ul>
            </div>

            {/* Divider line */}
            <div className="h-px bg-slate-200" />

            {/* What you can expect – detailed */}
            <div>
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-sky-700">
                What you can expect
              </p>
              <p className="mt-1 text-[0.72rem] text-slate-600">
                A partnership model that feels like an extension of your own team, not a black-box
                vendor.
              </p>
              <ul className="mt-2 space-y-1.5 text-[0.76rem] text-slate-700">
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Dedicated engagement lead</span> as a single
                    point-of-contact for strategy, delivery and risk.
                  </span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Hybrid squads</span> blending data engineering,
                    ML, product and design so we can go from idea to interface.
                  </span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Shared backlog</span> in your tools (Jira,
                    Linear, Azure DevOps) with transparent priorities and estimates.
                  </span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Rhythm of communication</span> – weekly demos,
                    fortnightly deep-dives and monthly steering reviews.
                  </span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Full documentation</span> of architecture, data
                    models, pipelines and runbooks from day one.
                  </span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>
                    <span className="font-medium">Structured handover</span> with shadow/support
                    phases so your internal teams can own and extend what we build.
                  </span>
                </li>
              </ul>

              {/* Mini engagement snapshot */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-[0.7rem]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                  <div className="text-[0.65rem] uppercase tracking-[0.16em] text-slate-500">
                    Typical span
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">8–16 weeks</div>
                  <div className="mt-0.5 text-[0.65rem] text-slate-500">
                    From discovery to first production use-case.
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                  <div className="text-[0.65rem] uppercase tracking-[0.16em] text-slate-500">
                    Team size
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">3–8 people</div>
                  <div className="mt-0.5 text-[0.65rem] text-slate-500">
                    Blend of engineers, scientists &amp; PM.
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                  <div className="text-[0.65rem] uppercase tracking-[0.16em] text-slate-500">
                    Ways of working
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">Remote-first</div>
                  <div className="mt-0.5 text-[0.65rem] text-slate-500">
                    Shared Slack/Teams channels &amp; co-created docs.
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Right: active industry detail */}
          <article className="space-y-6 lg:space-y-8">
            {/* Header & snapshot */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-[0.7rem] uppercase tracking-[0.2em] text-sky-700">
                    {active.sectors.join(" • ")}
                  </p>
                  <h2 className="mt-2 text-xl md:text-2xl font-semibold text-slate-900">
                    {active.name}
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    {active.overview}
                  </p>
                </div>

                <div className="mt-1 grid gap-2 text-xs min-w-[200px]">
                  {active.snapshots.map((snap) => (
                    <div
                      key={snap.label}
                      className="rounded-xl bg-slate-50 px-3 py-2 border border-slate-200"
                    >
                      <div className="text-[0.68rem] uppercase tracking-[0.16em] text-slate-500">
                        {snap.label}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-sky-700">
                        {snap.value}
                      </div>
                      {snap.hint && (
                        <div className="mt-0.5 text-[0.68rem] text-slate-500">
                          {snap.hint}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Featured case study */}
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-sky-900 p-4 md:p-5 shadow-[0_18px_45px_rgba(15,23,42,0.7)] text-slate-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500/20 border border-sky-400/60">
                    <FileText className="h-4 w-4 text-sky-200" />
                  </span>
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] text-sky-300">
                      {active.caseStudy.label}
                    </p>
                    <h3 className="mt-1 text-sm md:text-base font-semibold text-white">
                      {active.caseStudy.title}
                    </h3>
                    <p className="mt-1 text-xs md:text-sm text-slate-200 max-w-2xl">
                      {active.caseStudy.summary}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start md:items-end gap-2 min-w-[180px]">
                  <div className="rounded-xl bg-sky-500/15 border border-sky-400/60 px-3 py-2">
                    <p className="text-[0.68rem] uppercase tracking-[0.18em] text-sky-200">
                      Headline outcome
                    </p>
                    <p className="mt-1 text-sm font-semibold text-sky-50">
                      {active.caseStudy.highlight}
                    </p>
                  </div>
                  <p className="text-[0.7rem] text-slate-200 max-w-xs md:text-right">
                    {active.caseStudy.result}
                  </p>
                  <div className="inline-flex items-center gap-1 text-[0.72rem] font-medium text-sky-200">
                    <span>Ask us to walk you through this story</span>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Challenges & pillars */}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">
                  Typical challenges we see
                </h3>
                <ul className="mt-3 space-y-2 text-xs md:text-[0.8rem] text-slate-700">
                  {active.challenges.map((c) => (
                    <li key={c} className="flex gap-2">
                      <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">
                  Solution pillars
                </h3>
                <div className="mt-3 grid gap-3 md:gap-4 md:grid-cols-2">
                  {active.pillars.map((p) => (
                    <div
                      key={p.title}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-3 md:p-4"
                    >
                      <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                        {p.title}
                      </h4>
                      <p className="mt-1 text-xs text-slate-700">
                        {p.description}
                      </p>
                      <ul className="mt-2 space-y-1.5 text-[0.72rem] text-slate-700">
                        {p.bullets.map((b) => (
                          <li key={b} className="flex gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-600 shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Use-cases */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-900">
                Reference use-cases &amp; impact
              </h3>
              <div className="mt-3 grid gap-3 md:gap-4 md:grid-cols-2">
                {active.useCases.map((u) => (
                  <div
                    key={u.title}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-3 md:p-4"
                  >
                    <h4 className="text-sm font-semibold text-slate-900">
                      {u.title}
                    </h4>
                    <p className="mt-1 text-xs text-slate-700">
                      {u.description}
                    </p>
                    <p className="mt-2 text-xs text-sky-800">
                      <span className="font-semibold">Impact: </span>
                      {u.impact}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tech, data & engagement */}
            <div className="grid gap-4 md:gap-6 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">
                  Data &amp; systems we connect
                </h3>
                <ul className="mt-3 space-y-1.5 text-[0.8rem] text-slate-700">
                  {active.dataSources.map((src) => (
                    <li key={src} className="flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-600 shrink-0" />
                      <span>{src}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">
                  Typical tech stack
                </h3>
                <ul className="mt-3 space-y-1.5 text-[0.8rem] text-slate-700">
                  {active.techStack.map((t) => (
                    <li key={t} className="flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-600 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900">
                  Engagement models
                </h3>
                <ul className="mt-3 space-y-1.5 text-[0.8rem] text-slate-700">
                  {active.engagementModels.map((m) => (
                    <li key={m} className="flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-600 shrink-0" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* WHY CHOOSE SECTION */}
      <section className="w-full bg-white py-12 md:py-16">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          <div className="text-center">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
              Why choose Technocolabs?
            </h2>
            <p className="mt-2 text-sm md:text-base text-slate-600 max-w-3xl mx-auto">
              Let our team of specialists help you turn data and AI into real outcomes across
              your industry – from strategy to production.
            </p>
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-sky-200 bg-sky-50">
                <LineChart className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="mt-4 text-sm md:text-base font-semibold text-slate-900">
                Industry-specific AI development
              </h3>
              <p className="mt-2 text-xs md:text-sm text-slate-600 max-w-xs">
                We design and build use-cases around your domain – financial services, healthcare,
                retail, manufacturing and more – instead of generic one-size-fits-all solutions.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-sky-200 bg-sky-50">
                <Trophy className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="mt-4 text-sm md:text-base font-semibold text-slate-900">
                Top tech talent on demand
              </h3>
              <p className="mt-2 text-xs md:text-sm text-slate-600 max-w-xs">
                Blended squads of data engineers, ML engineers and product thinkers who have
                shipped production systems for fast-growing startups and large enterprises.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-sky-200 bg-sky-50">
                <Star className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="mt-4 text-sm md:text-base font-semibold text-slate-900">
                A trustworthy delivery partner
              </h3>
              <p className="mt-2 text-xs md:text-sm text-slate-600 max-w-xs">
                Transparent communication, on-time delivery and clear success metrics, so you
                always know where the project stands and what value it&apos;s creating.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ SECTION */}
      <section className="w-full bg-slate-50 py-10 md:py-12 border-t border-slate-200">
        <div className="w-full px-4 sm:px-8 lg:px-16">
          {/* Centered heading */}
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.22em] text-sky-700">
              Industries FAQ
            </p>
            <h2 className="mt-2 text-lg md:text-xl font-semibold text-slate-900">
              Questions teams often ask us.
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              If you don&apos;t see your question here, just mention it in the contact form –
              we&apos;re happy to walk you through details.
            </p>
          </div>

          {/* Centered accordion */}
          <div className="mt-6 max-w-3xl mx-auto space-y-3">
            {INDUSTRY_FAQ.map((item, idx) => {
              const open = openFaq === idx;
              return (
                <button
                  key={item.question}
                  type="button"
                  onClick={() => setOpenFaq(open ? null : idx)}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 md:px-5 md:py-4 shadow-sm hover:border-sky-300 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-900">
                      {item.question}
                    </p>
                    <span className="shrink-0">
                      {open ? (
                        <Minus className="h-4 w-4 text-slate-500" />
                      ) : (
                        <Plus className="h-4 w-4 text-slate-500" />
                      )}
                    </span>
                  </div>
                  {open && (
                    <p className="mt-2 text-xs md:text-sm text-slate-700">
                      {item.answer}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>


      {/* Cross-industry accelerators */}
      <section className="w-full border-t border-slate-200 bg-gradient-to-r from-sky-50 via-white to-indigo-50">
        <div className="w-full px-4 sm:px-8 lg:px-16 py-8 md:py-10">
          <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] items-center">
            <div>
              <p className="text-xs tracking-[0.22em] uppercase text-sky-700">
                Cross-industry accelerators
              </p>
              <h2 className="mt-2 text-xl md:text-2xl font-semibold text-slate-900">
                Ready-to-use building blocks, adapted to your context.
              </h2>
              <p className="mt-3 text-sm text-slate-600 max-w-xl">
                Over multiple projects we have built patterns, components and reference
                architectures that shorten time-to-value while keeping you in full control of
                your IP and roadmap.
              </p>
              <ul className="mt-4 space-y-2.5 text-xs text-slate-700">
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>Pre-designed data models and semantic layers for common domains.</span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>Reusable components for feature stores, MLOps and observability.</span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="mt-[2px] h-3.5 w-3.5 text-sky-600 shrink-0" />
                  <span>Reference playbooks for governance, data quality and change-management.</span>
                </li>
              </ul>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 text-xs">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                  POV &amp; Roadmap
                </p>
                <p className="mt-2 font-semibold text-slate-900">
                  2–4 week deep-dives to prioritise the right bets.
                </p>
                <p className="mt-2 text-[0.75rem] text-slate-600">
                  Ideal when you need a sharp outside-in view before committing to larger
                  programs.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                  Pilots &amp; MVPs
                </p>
                <p className="mt-2 font-semibold text-slate-900">
                  Bring 1–2 critical use-cases to life, end-to-end.
                </p>
                <p className="mt-2 text-[0.75rem] text-slate-600">
                  From data ingestion and modelling to UX, change-management and training.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                  Long-term squads
                </p>
                <p className="mt-2 font-semibold text-slate-900">
                  Dedicated, blended teams across data, AI and engineering.
                </p>
                <p className="mt-2 text-[0.75rem] text-slate-600">
                  Ideal when you want to accelerate while building strong in-house capability.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-300 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-sky-700">
                Talk to our team
              </p>
              <p className="mt-1 text-sm md:text-base text-slate-900">
                Not sure where to start? Share your context and we&apos;ll co-design a path
                that balances quick wins with long-term foundations.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-full border border-sky-600 bg-sky-600 px-4 py-2 text-xs md:text-sm font-semibold text-white shadow-md hover:bg-sky-700 transition"
              >
                Book a 30-min discovery call
              </a>
              <a
                href="mailto:hello@technocolabs.com"
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-xs md:text-sm text-slate-800 hover:bg-slate-50"
              >
                Or email us your brief
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
